import { useCallback, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MonetisationApi } from 'src/api';
import { FeatureTokenType } from 'src/api/monetisation/types';
import { useExperienceContext } from 'src/context/experience';
import { useUserContext } from 'src/context/user';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';

export const useActivityCheck = (params: {
  enabled?: boolean;
  tokenType: FeatureTokenType | null;
  languagePairId?: number | null;
  refreshOnMount?: boolean;
}) => {
  /* istanbul ignore next: please test in WEBSITE-7219 */
  const enabledFromParam = params.enabled || !('enabled' in params);
  const { tokenType } = params;
  const { user } = useUserContext();
  const languagePairId = params.languagePairId ?? getLanguagePairId();
  const { experienceType } = useExperienceContext();
  const hasConsumedToken = useRef(false);

  const shouldCheckAvailability =
    enabledFromParam && experienceType === '2025' && !user?.isPro && !!tokenType;

  const queryEnabled = !!languagePairId && shouldCheckAvailability;

  const {
    data: features,
    isError: isAvailabilityQueryError,
    isFetching,
  } = useQuery({
    queryKey: ['getFeaturesTokensAvailability', languagePairId],
    queryFn: () => MonetisationApi.getFeaturesTokensAvailability(languagePairId as number),
    enabled: queryEnabled,
    refetchOnMount: params.refreshOnMount ? 'always' : true,
  });

  const tokenAvailability = useMemo(() => {
    if (!features) return null;

    const feature = features?.find(f => f.type === tokenType);

    if (!feature) return 'not-found';

    return feature.available ? 'available' : 'not-available';
  }, [features, tokenType]);

  const onActivityEngagement = useCallback(() => {
    if (!hasConsumedToken.current && shouldCheckAvailability && tokenAvailability === 'available') {
      hasConsumedToken.current = true;
      void MonetisationApi.consumeFeatureToken(languagePairId as number, tokenType);
    }
  }, [languagePairId, shouldCheckAvailability, tokenAvailability, tokenType]);

  const isAvailabilityError = queryEnabled && isAvailabilityQueryError;

  const isDataLoaded = !shouldCheckAvailability || isAvailabilityError || tokenAvailability != null;

  const isRefreshing =
    params.refreshOnMount && queryEnabled && isFetching && !hasConsumedToken.current;
  const isLoading = !enabledFromParam
    ? false
    : experienceType === '2024'
      ? false
      : !isDataLoaded || !!isRefreshing;

  return {
    isLoading,
    isError: isAvailabilityError,
    isLocked: shouldCheckAvailability && tokenAvailability === 'not-available',
    /** Whether this user's access to the activity is rationed at all, so the
     * UI can tell them how much of today's free allowance is left. */
    isRationed: shouldCheckAvailability,
    onActivityEngagement,
  };
};
